import React from 'react';
import { Input, Button, Form, Card, message, DatePicker, Select, Radio } from 'antd';
import { FormInstance } from 'antd/lib/form';
import BreadcrumbCustom from '../../components/widget/BreadcrumbCustom';
import './page-requirement.less';
import RequirementService, { RequirementPostParam, RequirementPutParam, IRequirementInfo } from '../../service/requirement/RequirementService';
import {
  ADD_CONTACT_SUCCESS_MESSAGE,
  ADD_CONTACT_ERROR_MESSAGE,
  EDIT_CONTACT_ERROR_MESSAGE,
  EDIT_CONTACT_SUCCESS_MESSAGE,
  GET_CONTACT_ERROR_MESSAGE,
  GET_CUSTOMER_ERROR_MESSAGE,
} from '../../config/strcfg';
import ErrorBoundary from 'antd/lib/alert/ErrorBoundary';
import moment from 'moment';
import SearchSelectComponent, { SearchSelectOption } from '../../components/public/SearchSelectComponent';
import CustomerService, { ICustomerInfo } from '../../service/customer/CustomerService';
import CompanyService, { CompanyListRequest, ICompanyInfoList } from '../../service/company/CompanyService';
import CustomerManagerService, { CustomerManagerListRequest, ICustomerManagerInfoList } from '../../service/customerManager/CustomerManagerService';
import AdminService from '../../service/admin/AdminService';

type DemandType = 'internal' | 'external';

interface RequirementManagerPageState {
  loading: boolean;
  isEditMode: boolean;
  isAdminCreateMode: boolean;
  demandType: DemandType;
  requirementData: IRequirementInfo | null;
  customerData: ICustomerInfo[];
  companyOptions: SearchSelectOption[];
  internalSalesOptions: SearchSelectOption[];
  externalSalesOptions: SearchSelectOption[];
}

const parseIntParam = (value: string | null): number | null => {
  if (!value) {
    return null;
  }
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

// 从 URL 中获取参数
const getUrlParams = () => {
  const hash = window.location.hash;
  const queryString = hash.includes('?') ? hash.split('?')[1] : '';
  const urlParams = new URLSearchParams(queryString);
  const rawCustomerName = urlParams.get('customerName');

  return {
    customerName: rawCustomerName ? decodeURIComponent(rawCustomerName) : null,
    customerId: parseIntParam(urlParams.get('customer')),
    companyId: parseIntParam(urlParams.get('companyId')),
    requirementId: parseIntParam(urlParams.get('id')),
  };
};

class RequirementManagerPage extends React.Component<any, RequirementManagerPageState> {
  state: RequirementManagerPageState = {
    loading: false,
    isEditMode: false,
    isAdminCreateMode: false,
    demandType: 'external',
    requirementData: null,
    customerData: [],
    companyOptions: [],
    internalSalesOptions: [],
    externalSalesOptions: [],
  };

  formRef = React.createRef<FormInstance>();

  isAdminCreateEntry = () => window.location.hash.startsWith('#/app/customer/requirement/new');

  async componentDidMount() {
    const { requirementId, companyId, customerId, customerName } = getUrlParams();
    const isAdminCreateMode = this.isAdminCreateEntry() && !requirementId;
    this.setState({ isAdminCreateMode });

    if (isAdminCreateMode) {
      await Promise.all([
        this.loadCompanyOptions(),
        this.loadInternalSalesOptions(),
        this.loadExternalSalesOptions(),
      ]);
      this.formRef.current?.setFieldsValue({ demandType: 'external' });
    }

    if (requirementId) {
      await this.loadEditData(requirementId);
      return;
    }

    this.setState({ isEditMode: false });

    if (isAdminCreateMode) {
      return;
    }

    if (!companyId) {
      message.error(GET_CUSTOMER_ERROR_MESSAGE);
      return;
    }

    this.formRef.current?.setFieldsValue({
      companyId,
      customer: customerId || undefined,
      customerName: customerName || undefined,
    });
    await this.loadCustomerByCompanyId(companyId, customerId, customerName || undefined);
  }

  loadEditData = async (requirementId: number) => {
    this.setState({ isEditMode: true, loading: true });
    try {
      const response = await RequirementService.getRequirementById(requirementId);
      if (!(response && response.data && response.data.requirementInfo)) {
        this.setState({ requirementData: null });
        message.error(GET_CONTACT_ERROR_MESSAGE);
        return;
      }

      const requirementData: IRequirementInfo = response.data.requirementInfo;
      this.setState({ requirementData });
      this.formRef.current?.setFieldsValue({
        name: requirementData.name,
        companyId: requirementData.companyId,
        companyName: requirementData.companyName,
        customer: requirementData.customer,
        customerName: requirementData.customerName,
        sales: requirementData.sales,
        salesName: requirementData.salesName,
        deliveryTime: requirementData.deliveryTime ? moment(requirementData.deliveryTime) : undefined,
        budget: requirementData.budget,
        states: requirementData.states,
        introduction: requirementData.introduction,
        createTime: requirementData.createTime ? moment(requirementData.createTime) : undefined,
      });

      if (requirementData.companyId) {
        await this.loadCustomerByCompanyId(requirementData.companyId, requirementData.customer, requirementData.customerName);
      }
    } catch (error) {
      message.error(GET_CONTACT_ERROR_MESSAGE);
    } finally {
      this.setState({ loading: false });
    }
  };

  loadCompanyOptions = async () => {
    const request: CompanyListRequest = {
      companyQueryParam: {},
      pageNo: 1,
      pageSize: 999999,
    };

    try {
      const response = await CompanyService.getCompanyListInfo(request);
      const pageData: ICompanyInfoList = response?.data?.companyInfoList || ([] as any);
      const companyRecords = pageData?.records || [];
      const companyOptions = companyRecords.map((item: any) => ({
        key: item.id,
        value: item.name,
      }));
      this.setState({ companyOptions });
    } catch (error) {
      this.setState({ companyOptions: [] });
    }
  };

  loadInternalSalesOptions = async () => {
    try {
      const response = await AdminService.getSalesListInfo();
      const records = response?.data?.salesInfo || [];
      const internalSalesOptions = records.map((item: any) => ({
        key: item.id,
        value: item.realName || item.account,
      }));
      this.setState({ internalSalesOptions });
    } catch (error) {
      this.setState({ internalSalesOptions: [] });
    }
  };

  loadExternalSalesOptions = async () => {
    const request: CustomerManagerListRequest = {
      queryParam: {
        managerType: 2,
        states: 0,
        includeDeleted: false,
      },
      pageNo: 1,
      pageSize: 999999,
    };

    try {
      const response = await CustomerManagerService.getCustomerManagerList(request);
      const pageData: ICustomerManagerInfoList = response?.data?.customerManagerInfoList || ([] as any);
      const records = pageData?.records || [];
      const externalSalesOptions = records.map((item: any) => ({
        // 外部客户经理使用负值编码到 requirement.sales，避免与 admin.id 冲突
        key: -Math.abs(Number(item.id)),
        value: item.name,
      }));
      this.setState({ externalSalesOptions });
    } catch (error) {
      this.setState({ externalSalesOptions: [] });
    }
  };

  loadCustomerByCompanyId = async (companyId: number, presetCustomerId?: number | null, presetCustomerName?: string) => {
    try {
      const response = await CustomerService.getCustomerByCompanyId(companyId, { pageNo: 1, pageSize: 99999 });
      const customerRecords = response?.data?.customerInfoList?.records || [];
      const customerData = customerRecords.map((item: any) => ({
        id: item.id,
        cname: item.cname,
      }));

      this.setState({ customerData }, () => {
        if (presetCustomerId) {
          this.formRef.current?.setFieldsValue({
            customer: presetCustomerId,
            customerName: presetCustomerName || '',
          });
        }
      });
    } catch (error) {
      this.setState({ customerData: [] });
      message.error(GET_CUSTOMER_ERROR_MESSAGE);
    }
  };

  // 返回按钮点击事件处理
  handleBackButtonClick = () => {
    window.history.back();
  };

  handleDemandTypeChange = (value: DemandType) => {
    this.setState({ demandType: value, customerData: [] });
    this.formRef.current?.setFieldsValue({
      companyId: undefined,
      companyName: undefined,
      customer: undefined,
      customerName: undefined,
      sales: undefined,
    });
  };

  handleInternalCompanyChange = (companyId?: number) => {
    this.formRef.current?.setFieldsValue({
      companyId: companyId || undefined,
      customer: undefined,
      customerName: undefined,
    });

    if (!companyId) {
      this.setState({ customerData: [] });
      return;
    }

    this.loadCustomerByCompanyId(companyId);
  };

  onFinish = async (params: any) => {
    this.setState({ loading: true });

    const submitParams: RequirementPostParam = { ...params };

    if (this.state.isAdminCreateMode && !this.state.isEditMode) {
      if (this.state.demandType === 'internal') {
        delete (submitParams as any).companyName;
        delete (submitParams as any).customerName;
      } else {
        delete (submitParams as any).companyId;
        delete (submitParams as any).customer;
      }
      delete (submitParams as any).demandType;
    }

    //调用addRequirement接口
    if (!this.state.isEditMode) {
      try {
        const response = await RequirementService.addRequirement(submitParams as RequirementPostParam);
        if (response && response.success) {
          this.formRef.current?.resetFields();
          this.setState({ customerData: [] });
          if (this.state.isAdminCreateMode) {
            this.formRef.current?.setFieldsValue({ demandType: 'external' });
            this.setState({ demandType: 'external' });
          }
          message.success(ADD_CONTACT_SUCCESS_MESSAGE);
        } else {
          message.error(response.message);
        }
      } catch (error) {
        message.error(ADD_CONTACT_ERROR_MESSAGE);
      } finally {
        this.setState({ loading: false });
      }
      return;
    }

    //调用editRequirement接口
    try {
      const updatedParams = {
        ...submitParams,
        id: this.state.requirementData!.id,
      };
      const response = await RequirementService.updateRequirement(updatedParams as RequirementPutParam);
      if (response && response.success) {
        this.formRef.current?.resetFields();
        message.success(EDIT_CONTACT_SUCCESS_MESSAGE);
        setTimeout(() => {
          window.location.reload();
        }, 500);
      } else {
        this.formRef.current?.resetFields();
        message.error(response.message);
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } catch (error) {
      this.formRef.current?.resetFields();
      message.error(EDIT_CONTACT_ERROR_MESSAGE);
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } finally {
      this.setState({ loading: false });
    }
  };

  renderAdminCreateFields = () => {
    const { demandType, companyOptions, internalSalesOptions, externalSalesOptions, customerData } = this.state;
    const isInternal = demandType === 'internal';

    return (
      <>
        <Form.Item label="需求类型" name="demandType" className="formItem-name-input">
          <Radio.Group onChange={(e) => this.handleDemandTypeChange(e.target.value)}>
            <Radio value="external">外部需求</Radio>
            <Radio value="internal">内部需求</Radio>
          </Radio.Group>
        </Form.Item>

        {isInternal ? (
          <Form.Item
            label="客户公司"
            name="companyId"
            className="formItem-name-input"
            rules={[{ required: true, message: '请选择客户公司' }]}
          >
            <Select
              showSearch
              optionFilterProp="children"
              placeholder="请选择客户公司"
              allowClear
              onChange={(value) => this.handleInternalCompanyChange(value as number | undefined)}
            >
              {companyOptions.map(option => (
                <Select.Option key={option.key} value={option.key}>{option.value}</Select.Option>
              ))}
            </Select>
          </Form.Item>
        ) : (
          <Form.Item
            label="客户公司"
            name="companyName"
            className="formItem-name-input"
            rules={[{ required: true, message: '请输入客户公司' }]}
          >
            <Input placeholder="请输入客户公司" autoComplete="off" />
          </Form.Item>
        )}

        <Form.Item
          label="客户经理"
          name="sales"
          className="formItem-name-input"
          rules={[{ required: true, message: '请选择客户经理' }]}
        >
          <Select
            showSearch
            optionFilterProp="children"
            placeholder="请选择客户经理"
            allowClear
          >
            {(isInternal ? internalSalesOptions : externalSalesOptions).map(option => (
              <Select.Option key={option.key} value={option.key}>{option.value}</Select.Option>
            ))}
          </Select>
        </Form.Item>

        {isInternal ? (
          <Form.Item
            label="对接人"
            name="customer"
            className="formItem-name-input"
            rules={[{ required: true, message: '请选择需求对接人' }]}
          >
            <SearchSelectComponent
              placeholder={this.formRef.current?.getFieldValue('companyId') ? '请选择对接人' : '请先选择客户公司'}
              optionProps={customerData.map(customer => ({ key: customer.id, value: customer.cname }))}
              onChange={(key, value) => {
                this.formRef.current?.setFieldsValue({
                  customer: key,
                  customerName: value,
                });
              }}
              allowClear={false}
              disabled={!this.formRef.current?.getFieldValue('companyId')}
            />
          </Form.Item>
        ) : (
          <Form.Item
            label="对接人"
            name="customerName"
            className="formItem-name-input"
            rules={[{ required: true, message: '请输入需求对接人' }]}
          >
            <Input placeholder="请输入需求对接人" autoComplete="off" />
          </Form.Item>
        )}
      </>
    );
  };

  render() {
    const { isEditMode, requirementData, isAdminCreateMode } = this.state;
    const { companyId, customerId, customerName } = getUrlParams();

    //form 初始值
    const initialValues = isEditMode && requirementData
      ? {
        name: requirementData.name || '',
        companyId: requirementData.companyId || '',
        companyName: requirementData.companyName || '',
        customer: requirementData.customer || '',
        customerName: requirementData.customerName || '',
        sales: requirementData.sales || '',
        salesName: requirementData.salesName || '',
        deliveryTime: requirementData.deliveryTime ? moment(requirementData.deliveryTime) : undefined,
        budget: requirementData.budget || '',
        states: requirementData.states || '',
        introduction: requirementData.introduction || '',
        createTime: requirementData.createTime ? moment(requirementData.createTime) : undefined,
      }
      : {
        demandType: 'external',
        customerName: customerName || '',
        companyId: companyId || '',
        customer: customerId || '',
      };

    const customerOptions = this.state.customerData.map(customer => ({
      key: customer.id,
      value: customer.cname,
      label: customer.cname,
    }));

    const cardTitle = isEditMode ? '编辑需求信息' : (isAdminCreateMode ? '新建需求' : '添加需求信息');

    return this.state.loading === false ? (
      <ErrorBoundary>
        <div className="page-requirement_container">
          <BreadcrumbCustom breads={['需求沟通信息', cardTitle]} />
          <Card
            title={
              <div className="title-manager_page">
                <Button type="link" className="back-button" onClick={this.handleBackButtonClick}>{'< 返回'}</Button>
                {cardTitle}
              </div>
            }
            bordered={false}
          >
            <div className="add-requirement_container">
              <Form ref={this.formRef} onFinish={this.onFinish} initialValues={initialValues}>
                {!isAdminCreateMode && (
                  <Form.Item name="sales" hidden>
                    <Input type="hidden" />
                  </Form.Item>
                )}

                {!isAdminCreateMode && (
                  <Form.Item name="companyId" hidden>
                    <Input type="hidden" />
                  </Form.Item>
                )}

                {!isEditMode && isAdminCreateMode ? this.renderAdminCreateFields() : (
                  <Form.Item
                    label="对接人"
                    name="customer"
                    className="formItem-customer"
                    rules={[{ required: true, message: '请选择需求对接人' }]}
                  >
                    <SearchSelectComponent
                      placeholder="请选择对接人"
                      optionProps={customerOptions}
                      initialValues={initialValues.customerName}
                      onChange={(key, value) => {
                        this.formRef.current?.setFieldsValue({
                          customer: key,
                          customerName: value,
                        });
                      }}
                      allowClear={false}
                    />
                  </Form.Item>
                )}

                <Form.Item label="需求名称" name="name" className="formItem-name-input" rules={[{ required: true, message: '请输入需求名称' }]}>
                  <Input placeholder="请输入需求名称" autoComplete="off" />
                </Form.Item>

                <Form.Item label="需求介绍" name="introduction" className="formItem-content" rules={[{ required: true, message: '请输入需求介绍' }]}>
                  <Input.TextArea placeholder="请输入需求介绍" autoComplete="off" className="textarea-content" rows={4} />
                </Form.Item>

                <Form.Item
                  label="创建时间"
                  name="createTime"
                  className="formItem-delivery"
                  rules={[{ required: true, message: '请选择创建时间' }]}
                >
                  <DatePicker style={{ width: '73%' }} placeholder="需求创建时间" format="YYYY-MM-DD" />
                </Form.Item>

                <Form.Item label="交付时间" name="deliveryTime" className="formItem-delivery" rules={[{ required: false }]}> 
                  <DatePicker style={{ width: '73%' }} placeholder="需求交付时间" format="YYYY-MM-DD" />
                </Form.Item>

                <Form.Item label="需求预算" name="budget" className="formItem-budget-input" rules={[{ required: false }]}> 
                  <Input.Group compact>
                    <Form.Item name="budget" noStyle>
                      <Input style={{ width: 'calc(100% - 30px)' }} placeholder="请输入需求预算" autoComplete="off" />
                    </Form.Item>
                    <span style={{ lineHeight: '32px', padding: '0 8px' }}>元</span>
                  </Input.Group>
                </Form.Item>

                <Form.Item>
                  <Button type="primary" className="button-requirement_submit" htmlType="submit" loading={this.state.loading}>
                    提交
                  </Button>
                </Form.Item>
              </Form>
            </div>
          </Card>
        </div>
      </ErrorBoundary>
    ) : null;
  }
}

export default RequirementManagerPage;
